/**
 * Reglas del borrador de plantilla que el front aplica ANTES de validar. Puras.
 *
 * La validación de verdad es del backend (`POST /plantillas/validar`, una sola fuente de verdad).
 * Acá sólo va lo que el operador no tendría por qué acordarse (el pie y el botón de baja en
 * marketing), la previa instantánea mientras llega la del backend, y la decisión fail-closed de
 * si se puede mandar a Meta.
 */

import type {
  BotonPlantilla, Categoria, ConfigCuenta, ModeloPlantilla, PlantillaBorrador, PlantillaDetalle,
  Proposito, ReglaResultado, ResultadoValidacionApi, TipoPlantilla, VariablePlantilla,
} from "../types";
import { detectarNumeros, reemplazarVariables } from "./variables";

/** El id reservado del botón de baja (mismo que `ID_BOTON_BAJA` del backend). */
export const ID_BOTON_BAJA = "BAJA";

/** Borrador vacío: promoción, Marketing, respuesta rápida, en el idioma de la marca (se elige sólo en Configuración). */
export function borradorVacio(config: Pick<ConfigCuenta, "idioma">): PlantillaBorrador {
  return {
    nombreVisible: "",
    proposito: "promocion",
    categoria: "MARKETING",
    tipo: "twilio/quick-reply",
    idioma: config.idioma,
    cuerpo: "",
    botones: [],
    variables: [],
  };
}

/** Saca las claves opcionales vacías, así dos borradores iguales tienen la misma huella. */
function limpiar(b: PlantillaBorrador): PlantillaBorrador {
  const out: PlantillaBorrador = {
    nombreVisible: b.nombreVisible,
    proposito: b.proposito,
    categoria: b.categoria,
    tipo: b.tipo,
    idioma: b.idioma,
    cuerpo: b.cuerpo,
    botones: (b.botones || []).map((x) => ({ ...x })),
    variables: (b.variables || []).map((v) => ({ ...v })),
  };
  if (b.pie !== undefined) out.pie = b.pie;
  if (b.mediaUrl !== undefined) out.mediaUrl = b.mediaUrl;
  if (b.encabezadoTexto !== undefined) out.encabezadoTexto = b.encabezadoTexto;
  return out;
}

/** El modelo sin `clave` ni `descripcion`. */
export function desdeModelo(m: ModeloPlantilla): PlantillaBorrador {
  const { clave: _c, descripcion: _d, ...resto } = m;
  return limpiar(resto);
}

/** El borrador que corresponde a una plantilla guardada. La categoría es la SOLICITADA: si Meta la recategorizó, el borrador sigue siendo el que se escribió. */
export function desdeDetalle(p: PlantillaDetalle): PlantillaBorrador {
  return limpiar({
    nombreVisible: p.nombreVisible,
    proposito: p.proposito,
    categoria: p.categoriaSolicitada,
    tipo: p.tipo,
    idioma: p.idioma,
    cuerpo: p.cuerpo,
    pie: p.pie,
    mediaUrl: p.mediaUrl,
    encabezadoTexto: p.encabezadoTexto,
    botones: p.botones,
    variables: p.variables,
  });
}

/**
 * Tipo que corresponde en Marketing. Sólo hay dos tipos donde entra el botón de baja: respuesta
 * rápida y la tarjeta `whatsapp/card` (D-A). Si el operador venía con otro tipo, se lo lleva al
 * que conserva lo que ya cargó: la imagen o los links pasan a tarjeta (que tiene encabezado de
 * imagen y botones de link); el texto solo, a respuesta rápida.
 */
function tipoParaMarketing(t: TipoPlantilla): TipoPlantilla {
  if (t === "twilio/quick-reply" || t === "whatsapp/card") return t;
  if (t === "twilio/media" || t === "twilio/call-to-action") return "whatsapp/card";
  return "twilio/quick-reply";
}

/**
 * Aplica las reglas de categoría ANTES de validar. Pura.
 *  - MARKETING: la baja se PRECARGA, no se impone (pedido de Alan del 16/10/2026: cada promoción elige
 *    su texto de baja, y se puede mandar sin él; el validador avisa). Se precarga cuando la plantilla es
 *    nueva (sin `anterior`) o cuando pasa a ser promoción: tipo quick-reply o card, el pie de
 *    Configuración (en una nueva, sólo si no trae uno propio) y el botón
 *    {tipo:"QUICK_REPLY", id:"BAJA", titulo: config.botonBaja} UNA vez y ÚLTIMO. Con `anterior` en
 *    promoción no se toca nada: lo que el operador cambió o sacó se respeta.
 *  - UTILITY: saca el botón BAJA y, si el pie es exactamente textoBaja, lo vacía. No toca lo demás.
 * `anterior` es la versión del borrador de antes del cambio (el editor la pasa en cada cambio, y al abrir
 * una plantilla ya guardada pasa la misma, para no precargarle lo que el operador ya había sacado).
 */
export function aplicarReglasDeCategoria(
  b: PlantillaBorrador,
  config: Pick<ConfigCuenta, "textoBaja" | "botonBaja">,
  anterior?: PlantillaBorrador,
): PlantillaBorrador {
  const sinBaja = (b.botones || []).filter((x) => x.id !== ID_BOTON_BAJA);
  if (b.categoria === "MARKETING") {
    if (anterior && anterior.categoria === "MARKETING") return b;
    const paso = !!anterior; // pasó de utilidad a promoción: su pie no es de baja
    const baja: BotonPlantilla = { tipo: "QUICK_REPLY", id: ID_BOTON_BAJA, titulo: config.botonBaja };
    const pie = !paso && (b.pie || "").trim() ? b.pie : config.textoBaja;
    return { ...b, tipo: tipoParaMarketing(b.tipo), pie, botones: [...sinBaja, baja] };
  }
  const { pie, ...resto } = b;
  const out: PlantillaBorrador = { ...resto, botones: sinBaja };
  if (pie !== undefined && pie !== config.textoBaja) out.pie = pie;
  return out;
}

/** propósito → categoría: promocion/beneficio → MARKETING; aviso → la que eligió el operador (default UTILITY). */
export function categoriaPara(p: Proposito, elegida?: Categoria): Categoria {
  if (p === "promocion" || p === "beneficio") return "MARKETING";
  return elegida ?? "UTILITY";
}

/**
 * Tipos habilitados para la categoría: los 5 en las dos. Hasta el 16/10/2026 marketing iba sólo con
 * quick-reply o tarjeta, porque eran los tipos donde entra el botón de baja; desde que la baja no es
 * obligatoria, una promoción puede ir sin botones (el validador avisa).
 */
export function tiposHabilitados(_c: Categoria): TipoPlantilla[] {
  return ["twilio/text", "twilio/media", "twilio/quick-reply", "twilio/call-to-action", "whatsapp/card"];
}

/**
 * cuerpo.trimEnd() + (pie.trim() ? "\n\n" + pie.trim() : "") — la MISMA regla que `componerCuerpo`
 * del backend (§6.5; un pie de puros espacios no suma el párrafo). Con `whatsapp/card` el pie NO
 * va en el cuerpo (va en `footer`, D-A): el cuerpo compuesto es sólo el cuerpo.
 */
export function componerLocal(b: Pick<PlantillaBorrador, "cuerpo" | "pie"> & { tipo?: TipoPlantilla }): string {
  const cuerpo = (b.cuerpo || "").trimEnd();
  if (b.tipo === "whatsapp/card") return cuerpo;
  const pie = (b.pie || "").trim();
  return pie ? cuerpo + "\n\n" + pie : cuerpo;
}

/** Reemplaza cada {{n}} por el ejemplo de su variable, o por «[alias]» si todavía no tiene. */
export function conEjemplos(texto: string, b: Pick<PlantillaBorrador, "variables">): string {
  const porNumero = new Map((b.variables || []).map((v) => [v.numero, v]));
  return reemplazarVariables(texto, (n) => {
    const v = porNumero.get(n);
    if (v && v.ejemplo && v.ejemplo.trim()) return v.ejemplo;
    return `[${(v?.alias || "").trim() || `Variable ${n}`}]`;
  });
}

/**
 * La URL de la imagen con las variables de UN contacto, igual que `renderTexto` del backend (y que
 * Twilio con `ContentVariables`): la clave es el número como texto y, si falta, el `{{n}}` queda tal
 * cual. Así la previa de la campaña muestra la imagen que va a salir y no la URL de la plantilla.
 */
export function mediaConVariables(url: string | undefined, variables: Record<string, string> | undefined): string | undefined {
  if (!url) return undefined;
  const v = variables && typeof variables === "object" ? variables : {};
  return reemplazarVariables(url, (n) => (Object.prototype.hasOwnProperty.call(v, String(n)) ? String(v[String(n)]) : undefined));
}

/**
 * Previa instantánea mientras llega la del backend: el cuerpo compuesto con cada {{n}} reemplazado
 * por su ejemplo, o por «[alias]» si no hay. Con `whatsapp/card` es sólo el cuerpo: el pie y el
 * encabezado de texto (sin variables, D-A) se pasan aparte a `PreviaWhatsApp`.
 */
export function previaLocal(b: PlantillaBorrador): string {
  return conEjemplos(componerLocal(b), b);
}

/**
 * La aclaración de debajo de la previa: lo que se ve en el lugar de cada variable («Sofía») es el ejemplo que pide Meta
 * para aprobarla, no lo que le llega a cada persona. En la reunión con Llama Leads del 26/10/2026, Jean Pierre Caipo vio
 * «Hola Sofía» en la previa, no supo de dónde salía y escribió a mano el nombre del mensaje de prueba («Hola Alan»):
 * así le iba a llegar a todos. Sólo las variables del texto (las del link y la imagen no se ven). null si no hay ninguna
 * con ejemplo.
 */
export function notaDeEjemplos(b: Pick<PlantillaBorrador, "cuerpo" | "pie" | "variables"> & { tipo?: TipoPlantilla }): string | null {
  const enElTexto = detectarNumeros({ cuerpo: b.cuerpo, pie: b.pie, tipo: b.tipo, botones: [], mediaUrl: "" });
  const conEjemplo = enElTexto
    .map((n) => (b.variables || []).find((v) => v.numero === n))
    .filter((v): v is VariablePlantilla => !!v && !!(v.ejemplo || "").trim());
  if (!conEjemplo.length) return null;
  const cada = conEjemplo.map((v) => `«${v.ejemplo.trim()}» en ${(v.alias || "").trim() || `la variable ${v.numero}`}`);
  const lista = cada.length < 2 ? cada[0] : `${cada.slice(0, -1).join(", ")} y ${cada[cada.length - 1]}`;
  return `Se ve con los ejemplos que pide Meta para aprobarla: ${lista}. No le llegan a nadie: cada persona recibe su propio dato.`;
}

/** JSON con las claves ordenadas y sin `undefined`: el mismo borrador da siempre el mismo texto. */
function canonico(x: unknown): unknown {
  if (Array.isArray(x)) return x.map(canonico);
  if (x && typeof x === "object") {
    const o = x as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(o).sort()) if (o[k] !== undefined) out[k] = canonico(o[k]);
    return out;
  }
  return x;
}

/** Clave estable del borrador: sirve para saber si la validación corresponde a lo que hay en pantalla. */
export function huellaBorrador(b: PlantillaBorrador): string {
  return JSON.stringify(canonico(b));
}

/** Reglas con ok:false agrupadas por `campo` ("cuerpo", "pie", "botones", "mediaUrl", "variables", "nombreVisible", "_" para las sin campo). */
/**
 * El nombre de un dato del cliente para mostrar («Primer nombre»), con la etiqueta que manda el backend
 * (`GET /campos`). El detalle de una plantilla mostraba la clave cruda («primerNombre», «oferta»). Si la
 * etiqueta no vino, la clave; sin dato, «—».
 */
export function etiquetaDelCampo(campos: { campo: string; etiqueta: string }[], campo: string | null | undefined): string {
  if (!campo) return "—";
  return campos.find((c) => c.campo === campo)?.etiqueta || campo;
}

export function reglasFallidasPorCampo(reglas: ReglaResultado[]): Record<string, ReglaResultado[]> {
  const out: Record<string, ReglaResultado[]> = {};
  for (const r of reglas || []) {
    if (r.ok) continue;
    const k = r.campo || "_";
    (out[k] ||= []).push(r);
  }
  return out;
}

/**
 * Se puede mandar a aprobación: hay resultado, corresponde a la huella actual, no hay una validación
 * en curso y ninguna regla error con ok:false. Fail-closed: ante cualquier duda, no.
 */
export function puedeEnviarAAprobacion(i: {
  resultado: ResultadoValidacionApi | null; huellaResultado: string | null; huellaActual: string; validando: boolean;
}): boolean {
  if (i.validando || !i.resultado || !i.huellaResultado) return false;
  if (i.huellaResultado !== i.huellaActual) return false;
  if (!i.resultado.ok) return false;
  return !(i.resultado.reglas || []).some((r) => r.nivel === "error" && !r.ok);
}

/** Límite de caracteres del cuerpo compuesto según el tipo: 640 con call-to-action, 1024 si no (contador en pantalla; la regla la aplica el backend). */
export function limiteCuerpo(t: TipoPlantilla): number {
  return t === "twilio/call-to-action" ? 640 : 1024;
}

/**
 * id de un botón de respuesta rápida a partir del título: MAYÚSCULAS, sin acentos, [A-Z0-9_],
 * ≤ 200; nunca "BAJA" (→ "BAJA_2": es el que da de baja, y un botón cualquiera con ese id daría de
 * baja a quien lo toque); único dentro de la lista (sufijo _2, _3…).
 */
export function idBoton(titulo: string, existentes: string[]): string {
  const base = (titulo || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 200) || "BOTON";
  const usados = new Set(existentes);
  const libre = (c: string) => c !== ID_BOTON_BAJA && !usados.has(c);
  if (libre(base)) return base;
  for (let i = 2; ; i++) {
    const suf = `_${i}`;
    const c = base.slice(0, 200 - suf.length) + suf;
    if (libre(c)) return c;
  }
}

/** En revisión de Meta: recibida o pendiente (el filtro «En revisión» de la lista). */
export const enRevisionDeMeta = (estado: string): boolean => estado === "received" || estado === "pending";

/**
 * La lista de plantillas con las que están en revisión arriba de todo (pedido de Alan del 26/10/2026): son las que se
 * esperan. Dentro de cada grupo queda el orden que traía (la más nueva primero, como la manda el backend).
 */
export function primeroEnRevision<T extends { estado: string }>(lista: T[]): T[] {
  return [...lista].sort((a, b) => Number(enRevisionDeMeta(b.estado)) - Number(enRevisionDeMeta(a.estado)));
}
